import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getRecipes, createRecipe, updateRecipe, deleteRecipe, getFoods } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, ChevronLeft, ChevronRight, X, Loader2, GripVertical, Trash2 } from 'lucide-react';

const TAGS = ['breakfast', 'lunch', 'dinner', 'snack', 'vegan', 'quick', 'high-protein', 'high-fibre'];

const emptyRecipe = {
  name: '',
  emoji: '',
  gi: 50,
  carbsPerServing: 0,
  fibrePerServing: 0,
  proteinPerServing: 0,
  caloriesPerServing: 0,
  prepTimeMinutes: 30,
  diabeticType: 'both',
  tags: [],
  ingredients: [],
  steps: [],
  linkedFoodIds: [],
  whyGood: '',
  notes: '',
  isActive: true,
};

const RecipesPage = () => {
  const location = useLocation();
  const [recipes, setRecipes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [panelOpen, setPanelOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [formData, setFormData] = useState(emptyRecipe);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const [ingredientInput, setIngredientInput] = useState('');
  const [stepInput, setStepInput] = useState('');
  const [foodSearch, setFoodSearch] = useState('');
  const [foodResults, setFoodResults] = useState([]);
  const [allFoods, setAllFoods] = useState([]);

  const fetchRecipes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getRecipes({ page, limit: 25 });
      setRecipes(response.data.items);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load recipes');
    } finally {
      setIsLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchRecipes();
    // Load all foods for linking
    getFoods({ limit: 100 }).then(res => setAllFoods(res.data.items)).catch(() => {});
  }, [fetchRecipes]);

  useEffect(() => {
    if (location.state?.openNew) {
      openNewPanel();
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (foodSearch.trim()) {
      const filtered = allFoods.filter(f => 
        f.name.toLowerCase().includes(foodSearch.toLowerCase()) &&
        !formData.linkedFoodIds.includes(f.id)
      );
      setFoodResults(filtered.slice(0, 5));
    } else {
      setFoodResults([]);
    }
  }, [foodSearch, allFoods, formData.linkedFoodIds]);

  const openNewPanel = () => {
    setEditingRecipe(null);
    setFormData(emptyRecipe);
    setErrors({});
    setPanelOpen(true);
  };

  const openEditPanel = (recipe) => {
    setEditingRecipe(recipe);
    setFormData({
      ...recipe,
      tags: recipe.tags || [],
      ingredients: recipe.ingredients || [],
      steps: recipe.steps || [],
      linkedFoodIds: recipe.linkedFoodIds || [],
    });
    setErrors({});
    setPanelOpen(true);
  };

  const addIngredient = () => {
    if (ingredientInput.trim()) {
      setFormData({ ...formData, ingredients: [...formData.ingredients, ingredientInput.trim()] });
      setIngredientInput('');
    }
  };

  const removeIngredient = (index) => {
    setFormData({ ...formData, ingredients: formData.ingredients.filter((_, i) => i !== index) });
  };

  const addStep = () => {
    if (stepInput.trim()) {
      setFormData({ ...formData, steps: [...formData.steps, stepInput.trim()] });
      setStepInput('');
    }
  };

  const removeStep = (index) => {
    setFormData({ ...formData, steps: formData.steps.filter((_, i) => i !== index) });
  };

  const toggleTag = (tag) => {
    const newTags = formData.tags.includes(tag)
      ? formData.tags.filter(t => t !== tag)
      : [...formData.tags, tag];
    setFormData({ ...formData, tags: newTags });
  };

  const addLinkedFood = (food) => {
    setFormData({ ...formData, linkedFoodIds: [...formData.linkedFoodIds, food.id] });
    setFoodSearch('');
    setFoodResults([]);
  };

  const removeLinkedFood = (id) => {
    setFormData({ ...formData, linkedFoodIds: formData.linkedFoodIds.filter(fid => fid !== id) });
  };

  const getLinkedFoodName = (id) => {
    const food = allFoods.find(f => f.id === id);
    return food ? `${food.emoji} ${food.name}` : id;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = true;
    if (!formData.emoji.trim()) newErrors.emoji = true;
    if (!formData.whyGood.trim()) newErrors.whyGood = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      if (editingRecipe) {
        await updateRecipe(editingRecipe.id, formData);
        toast.success('Recipe updated successfully');
      } else {
        await createRecipe(formData);
        toast.success('Recipe created successfully');
      }
      setPanelOpen(false);
      fetchRecipes();
    } catch (error) {
      toast.error('Error saving recipe — try again');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingRecipe) return;
    setIsSaving(true);
    try {
      await deleteRecipe(editingRecipe.id);
      toast.success('Recipe deleted successfully');
      setPanelOpen(false);
      setDeleteDialogOpen(false);
      fetchRecipes();
    } catch (error) {
      toast.error('Error deleting recipe — try again');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="recipes-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Recipes</h1>
        <Button 
          onClick={openNewPanel}
          className="bg-[#1A6B3C] hover:bg-[#155a32]"
          data-testid="new-recipe-btn"
        >
          <Plus size={16} className="mr-1" /> New Recipe
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : recipes.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 mb-4">No recipes found</p>
            <Button onClick={openNewPanel} className="bg-[#1A6B3C] hover:bg-[#155a32]">
              <Plus size={16} className="mr-1" /> Add First Recipe
            </Button>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-center">GI</TableHead>
                  <TableHead className="text-center">Prep Time</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead className="text-center">Active</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipes.map((recipe) => (
                  <TableRow key={recipe.id} data-testid={`recipe-row-${recipe.id}`}>
                    <TableCell className="text-xl">{recipe.emoji}</TableCell>
                    <TableCell className="font-medium">{recipe.name}</TableCell>
                    <TableCell className="text-center">{recipe.gi}</TableCell>
                    <TableCell className="text-center">{recipe.prepTimeMinutes} min</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {recipe.tags?.slice(0, 3).map(tag => (
                          <span key={tag} className="text-xs bg-gray-100 px-2 py-0.5 rounded">{tag}</span>
                        ))}
                        {recipe.tags?.length > 3 && (
                          <span className="text-xs text-gray-400">+{recipe.tags.length - 3}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-block w-2 h-2 rounded-full ${recipe.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => openEditPanel(recipe)}>
                        <Pencil size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t">
              <span className="text-sm text-gray-500">
                Showing {recipes.length} of {total} recipes
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-sm">Page {page} of {totalPages}</span>
                <Button variant="outline" size="icon" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Panel */}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="slide-panel-520 overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingRecipe ? 'Edit Recipe' : 'New Recipe'}</SheetTitle>
            <SheetDescription>
              {editingRecipe ? 'Modify recipe details and instructions' : 'Create a new diabetic-friendly recipe'}
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label className={errors.name ? 'text-red-500' : ''}>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={errors.name ? 'border-red-500' : ''}
                />
              </div>
              <div>
                <Label className={errors.emoji ? 'text-red-500' : ''}>Emoji *</Label>
                <Input
                  value={formData.emoji}
                  onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
                  placeholder="🥗"
                  className={errors.emoji ? 'border-red-500' : ''}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <Label>GI *</Label>
                <Input type="number" min={0} max={100} value={formData.gi} onChange={(e) => setFormData({ ...formData, gi: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Carbs/serving *</Label>
                <Input type="number" step="0.1" value={formData.carbsPerServing} onChange={(e) => setFormData({ ...formData, carbsPerServing: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Fibre *</Label>
                <Input type="number" step="0.1" value={formData.fibrePerServing} onChange={(e) => setFormData({ ...formData, fibrePerServing: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Protein *</Label>
                <Input type="number" step="0.1" value={formData.proteinPerServing} onChange={(e) => setFormData({ ...formData, proteinPerServing: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label>Calories *</Label>
                <Input type="number" value={formData.caloriesPerServing} onChange={(e) => setFormData({ ...formData, caloriesPerServing: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Prep Time (min) *</Label>
                <Input type="number" value={formData.prepTimeMinutes} onChange={(e) => setFormData({ ...formData, prepTimeMinutes: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Diabetic Type</Label>
                <Select value={formData.diabeticType} onValueChange={(v) => setFormData({ ...formData, diabeticType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Both</SelectItem>
                    <SelectItem value="type1">Type 1</SelectItem>
                    <SelectItem value="type2">Type 2</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tags */}
            <div>
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TAGS.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      formData.tags.includes(tag) 
                        ? 'bg-[#1A6B3C] text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Ingredients */}
            <div>
              <Label>Ingredients</Label>
              <div className="space-y-2 mt-2">
                {formData.ingredients.map((ing, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    <GripVertical size={14} className="text-gray-400" />
                    <span className="flex-1 text-sm">{ing}</span>
                    <button onClick={() => removeIngredient(idx)} className="text-gray-400 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    value={ingredientInput}
                    onChange={(e) => setIngredientInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addIngredient())}
                    placeholder="Add ingredient..."
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="icon" onClick={addIngredient}>
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Steps */}
            <div>
              <Label>Steps</Label>
              <div className="space-y-2 mt-2">
                {formData.steps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-2 bg-gray-50 p-2 rounded">
                    <span className="text-xs font-bold text-[#1A6B3C] mt-0.5">{idx + 1}</span>
                    <span className="flex-1 text-sm">{step}</span>
                    <button onClick={() => removeStep(idx)} className="text-gray-400 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    value={stepInput}
                    onChange={(e) => setStepInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addStep())}
                    placeholder="Add step..."
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="icon" onClick={addStep}>
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Linked Foods */}
            <div>
              <Label>Linked Foods</Label>
              <div className="mt-2 space-y-2">
                <div className="flex flex-wrap gap-2">
                  {formData.linkedFoodIds.map(id => (
                    <span key={id} className="tag-chip">
                      {getLinkedFoodName(id)}
                      <button onClick={() => removeLinkedFood(id)}><X size={12} /></button>
                    </span>
                  ))}
                </div>
                <div className="relative">
                  <Input
                    value={foodSearch}
                    onChange={(e) => setFoodSearch(e.target.value)}
                    placeholder="Search foods to link..."
                  />
                  {foodResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-auto">
                      {foodResults.map(food => (
                        <button
                          key={food.id}
                          type="button"
                          onClick={() => addLinkedFood(food)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                        >
                          <span>{food.emoji}</span>
                          <span>{food.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label className={errors.whyGood ? 'text-red-500' : ''}>Why Good *</Label>
              <Input
                value={formData.whyGood}
                onChange={(e) => setFormData({ ...formData, whyGood: e.target.value })}
                className={errors.whyGood ? 'border-red-500' : ''}
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={formData.isActive} onCheckedChange={(v) => setFormData({ ...formData, isActive: v })} />
              <Label>Active</Label>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t">
              <Button onClick={handleSave} disabled={isSaving} className="bg-[#1A6B3C] hover:bg-[#155a32]">
                {isSaving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Save
              </Button>
              {editingRecipe && (
                <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} disabled={isSaving}>
                  Delete
                </Button>
              )}
              <Button variant="outline" onClick={() => setPanelOpen(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RecipesPage;
